import { Box, Paper, Avatar, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { CK, FONT_TITLE, FONT_BODY, sectionCardSx } from '../../pages/portal/portalStyles';

// Shared loading / empty / error / section-header primitives for the
// Organization Services portal.
//
// Before these existed every page hand-rolled its own version, which is why the
// console had four different loading treatments and a dozen empty states. These
// mirror the Prism Admin console exactly:
//   loading → accent spinner + Metropolis caption, vertically centered
//   empty   → 56px pastel avatar + title + one guiding sentence
//   error   → Alert with a Retry action and the page's own margins
//   section → 40px pastel avatar + Bitter 1.1rem title + 0.8rem subtitle

// PortalLoading renders the Admin console's loading block. Keep it INSIDE the
// page's card/section rather than returning it in place of the whole page —
// swapping the entire page for a spinner makes the header disappear and the
// layout jump when data lands.
export function PortalLoading({ label = 'Loading…', color = CK.blue, py = 6 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py }}>
      <CircularProgress size={28} sx={{ color, mb: 2 }} />
      <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body }}>
        {label}
      </Typography>
    </Box>
  );
}

// PortalEmptyState renders the Admin console's empty block: a pastel avatar,
// a title, and one sentence that tells the user what to do next.
export function PortalEmptyState({
  icon,
  title,
  description,
  action,
  color = CK.blue,
  tint,
  py = 6,
}) {
  return (
    <Box sx={{ textAlign: 'center', py, px: 2 }}>
      {icon && (
        <Avatar
          sx={{
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2,
            backgroundColor: tint || `${color}14`,
            color,
          }}
        >
          {icon}
        </Avatar>
      )}
      <Typography
        sx={{ fontFamily: FONT_TITLE, fontWeight: 600, fontSize: '1.1rem', color: CK.navy, mb: 0.5 }}
      >
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

// PortalErrorState renders a page-level error with a Retry affordance. It uses
// `mb` (not `m`) so the alert lines up with the cards above and below it —
// PortalLayout already supplies the page padding.
export function PortalErrorState({ error, onRetry, sx }) {
  const message = typeof error === 'string' ? error : error?.message || 'Something went wrong.';
  return (
    <Alert
      severity="error"
      sx={{
        mb: 3,
        borderRadius: '8px',
        fontFamily: FONT_BODY,
        '& .MuiAlert-message': { fontFamily: FONT_BODY },
        ...sx,
      }}
      action={
        onRetry ? (
          <Button
            color="inherit"
            size="small"
            onClick={onRetry}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
          >
            Retry
          </Button>
        ) : undefined
      }
    >
      {message}
    </Alert>
  );
}

// PortalSectionHeader renders the Admin console's in-card section header:
// 40px pastel avatar + Bitter title + muted subtitle, with an optional right
// action. The row wraps rather than crushing the action on narrow viewports.
export function PortalSectionHeader({ icon, title, subtitle, action, color = CK.blue, tint, mb = 2 }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        mb,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        {icon && (
          <Avatar
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              backgroundColor: tint || `${color}14`,
              color,
            }}
          >
            {icon}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontFamily: FONT_TITLE, fontWeight: 600, fontSize: '1.1rem', color: CK.navy }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted, fontWeight: 500 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
  );
}

// PortalSectionCard is the bordered `p:3` card every portal section sits in,
// with an optional PortalSectionHeader baked in.
export function PortalSectionCard({ icon, title, subtitle, action, color, tint, children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...sectionCardSx, ...sx }}>
      {title && (
        <PortalSectionHeader
          icon={icon}
          title={title}
          subtitle={subtitle}
          action={action}
          color={color}
          tint={tint}
        />
      )}
      {children}
    </Paper>
  );
}
